import React from 'react';
import {
  Buildings, CalendarBlank, Coins, EnvelopeSimple, File, FileArrowDown, IdentificationBadge, MapPin, Note, PencilSimpleLine, Phone,
  Receipt, Ticket as TicketIcon, UsersThree, type Icon,
} from '@phosphor-icons/react';
import { AnteilRing, Chip, Widget, WidgetWert } from '../../../../components/immobilien/ui';
import { EIG_PANEL_KOPF, type EigWidgetDef } from '../../../../components/immobilien/eigentuemerWidgets';
import { BriefeWidgetWert } from '../../dokumente/post/BriefVerlauf';
import { eur } from '../../../../lib/immobilien/format';
import { deDatum } from '../../../../lib/immobilien/objektDetail';
import { BUCHHALTUNG_FOLGT } from '../../../../lib/immobilien/hinweise';
import {
  hausgeldText, ibanKurz, kontoTeile, portfolioText, vertragsArt, vertragsDauer, zuordnungsZeilen,
} from '../../../../lib/eigentuemer/detail';
import { PORTAL_ANZEIGE } from '../../../../lib/eigentuemer/liste';
import { RHYTHMEN } from '../../../../lib/eigentuemer/sepaMandat';
import { freigabeKennzahlen } from '../../../../lib/eigentuemer/verwaltung';
import { bestellungStatus, istEtv } from '../../../../lib/eigentuemer/weg';
import { istAktuell } from '../../../../lib/eigentuemer/einheitEigentum';
import { artLabel, eigenerAnteil, textformWarnung, VERSANDWEGE, versandweg, zustellvertreterFehlt } from '../../../../lib/eigentuemer/beteiligte';
import type { AktivitaetTyp } from '../../../../lib/kontakte/types';
import type { EigSeitenWert } from './EigentuemerSeitenKontext';

const AKT_ICON: Record<AktivitaetTyp, Icon> = {
  email: EnvelopeSimple, ticket: TicketIcon, rechnung: Receipt, dokument: File, termin: CalendarBlank, notiz: Note,
  anruf: Phone, meeting: UsersThree, aenderung: PencilSimpleLine,
};
const tausend = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 2 });

/** Kleine Wert-Zeile: Icon · Text · rechts (Objekte, Kontakt, Kommunikation). */
function Zeile({ icon: IconKomp, farbe, text, rechts }: { icon: Icon; farbe: string; text: string; rechts?: string }) {
  return (
    <div className="flex items-center min-w-0" style={{ gap: 8, fontSize: 13 }}>
      <IconKomp size={15} color={farbe} style={{ flexShrink: 0 }} />
      <span className="flex-1 min-w-0 truncate" style={{ fontWeight: 600 }}>{text}</span>
      {rechts && <span className="immo-zahl" style={{ fontSize: 11, color: 'var(--ink3)' }}>{rechts}</span>}
    </div>
  );
}

const ChipZeile = ({ children }: { children: React.ReactNode }) => <span style={{ marginTop: 6 }}>{children}</span>;

export function EigentuemerWidget({ def, s, onOeffnen }: { def: EigWidgetDef; s: EigSeitenWert; onOeffnen: () => void }) {
  const kopf = EIG_PANEL_KOPF[def.key];
  const basis = { titel: kopf.widget, icon: kopf.icon, onOeffnen };
  const { daten, portfolio: p } = s;
  const z = daten.zeile;
  const w = z.werte;
  const e = daten.eigentuemer;

  switch (def.key) {
    case 'portfolio': {
      const ring = w.verwaltet
        ? <AnteilRing prozent={p.belegung} groesse={110} farbe="var(--acc-ink)" spur="var(--acc-ink-fill)" />
        : <AnteilRing prozent={Math.min(100, (p.mea / 1000) * 400)} groesse={110} farbe="var(--acc-ink)" spur="var(--acc-ink-fill)" text={`${tausend(p.mea)}‰`} />;
      return (
        <Widget {...basis} akzent>
          <div className="flex flex-1 items-center flex-wrap" style={{ gap: 16 }}>
            {ring}
            <div className="flex flex-col min-w-0" style={{ gap: 3 }}>
              <span className="immo-zahl" style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05 }}>
                {p.objekte === 0 ? 'Kein Objekt' : portfolioText(p)}
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--acc-ink2)' }}>
                {[p.flaeche ? `${tausend(p.flaeche)} m²` : '', w.verwaltet ? (p.belegung === null ? '' : `Belegung ${p.belegung} %`) : `MEA ${tausend(p.mea)} / 1.000`].filter(Boolean).join(' · ') || 'Noch keine Einheiten zugeordnet'}
              </span>
            </div>
          </div>
        </Widget>
      );
    }
    case 'objekte': {
      const zeilen = zuordnungsZeilen(z.zuordnungen);
      return (
        <Widget {...basis}>
          <div className="flex flex-1 flex-col justify-center min-w-0" style={{ gap: 7 }}>
            {zeilen.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Kein Objekt zugeordnet</span>}
            {zeilen.slice(0, 3).map(x => <Zeile key={x.key} icon={Buildings} farbe="var(--acc)" text={x.text} rechts={x.rolle} />)}
            {zeilen.length > 3 && <span style={{ fontSize: 11, color: 'var(--ink3)' }}>+ {zeilen.length - 3} weitere</span>}
          </div>
        </Widget>
      );
    }
    case 'konto': {
      const t = kontoTeile(w);
      return (
        <Widget {...basis}>
          {/* negativ: Hausgeld/Vergütung übersteigen die Miete – Unterdeckung (README §5) */}
          <WidgetWert wert={<span style={{ color: w.ausz < 0 ? 'var(--bad)' : 'var(--ok)' }}>{w.ausz < 0 ? 'Unterdeckung' : 'Auszahlung'} {eur(w.ausz, { nachkomma: 0 })}</span>}
            sub={t.text || 'Keine Mieteinnahmen'} />
          {t.balken.length > 0 && (
            <div className="flex overflow-hidden" style={{ height: 8, borderRadius: 4, gap: 2, marginTop: 6 }} aria-hidden>
              {t.balken.map((b, i) => <div key={i} style={{ width: `${b.anteil}%`, background: b.farbe }} />)}
            </div>
          )}
        </Widget>
      );
    }
    case 'mieter': {
      const leer = p.verwaltet - p.vermietet;
      return (
        <Widget {...basis}>
          <WidgetWert wert={p.vermietet} sub={p.verwaltet === 0 ? 'Keine verwalteten Einheiten' : leer ? `${leer} Leerstand` : 'voll vermietet'} />
          {leer > 0 && <ChipZeile><Chip groesse="xs" ton="warn">Leerstand</Chip></ChipZeile>}
        </Widget>
      );
    }
    case 'abrechnung':
      return <Widget {...basis}><WidgetWert wert="–" sub={BUCHHALTUNG_FOLGT} /></Widget>;
    case 'vertrag': {
      const chip = z.status === 'uebernahme' ? <Chip groesse="xs" ton="info">Übernahme</Chip>
        : z.endetAm ? <Chip groesse="xs" ton="warn">Endet {deDatum(z.endetAm)}</Chip> : null;
      return (
        <Widget {...basis}>
          <WidgetWert wert={vertragsDauer(z.beginn, s.heute)} sub={`${vertragsArt(w)} · ${w.nVerwaltet} WE`} />
          {chip && <ChipZeile>{chip}</ChipZeile>}
        </Widget>
      );
    }
    case 'freigaben': {
      const k = freigabeKennzahlen(daten.freigaben);
      return (
        <Widget {...basis}>
          <WidgetWert wert={<span style={{ color: k.offen ? 'var(--warn)' : 'var(--ok)' }}>{k.offen}</span>}
            sub={k.offen ? `${eur(k.volumen, { nachkomma: 0 })} warten auf Freigabe` : 'Keine offenen Freigaben'} />
        </Widget>
      );
    }
    case 'steuer': {
      const sid = String(e.steuer_id ?? '').trim();
      const snr = String(e.steuer_nr ?? '').trim();
      return <Widget {...basis}><WidgetWert wert="Anlage V" sub={sid ? `Steuer-ID ${sid.slice(0, 5)}…` : snr ? `Steuer-Nr. ${snr}` : 'Steuer-ID fehlt'} /></Widget>;
    }
    case 'hausgeld':
      return (
        <Widget {...basis}>
          <WidgetWert wert={`${eur(w.hgM, { nachkomma: 0 })} / Monat`} sub={w.hgM ? hausgeldText(w) : 'Kein Hausgeld hinterlegt'} />
          <span style={{ marginTop: 6, fontSize: 11, color: 'var(--ink3)' }}>Saldo: {BUCHHALTUNG_FOLGT}</span>
        </Widget>
      );
    case 'sondereigentum': {
      const n = z.zuordnungen.filter(x => x.rolle === 'WEG' || x.rolle === 'WEG+SEV').reduce((s2, x) => s2 + x.einheiten.length, 0);
      return <Widget {...basis}><WidgetWert wert={`${tausend(p.mea)} / 1.000`} sub={`MEA · ${n} ${n === 1 ? 'Einheit' : 'Einheiten'}`} /></Widget>;
    }
    case 'wegabrechnung':
      return <Widget {...basis}><WidgetWert wert="–" sub={BUCHHALTUNG_FOLGT} /></Widget>;
    case 'ruecklage': {
      const zufuehrung = Math.round(daten.einheitEig.filter(r => istAktuell(r, s.heute)).reduce((s2, r) => s2 + (Number(r.ruecklage_vorschuss) || 0), 0) * 100) / 100;
      return <Widget {...basis}><WidgetWert wert={zufuehrung ? `${eur(zufuehrung, { nachkomma: 0 })} / Monat` : '–'} sub={zufuehrung ? 'Zuführung laut Einheiten' : 'Keine Zuführung hinterlegt'} /></Widget>;
    }
    case 'etv': {
      const etv = daten.etvTermine[0] ?? daten.termine.find(t => istEtv(t));
      return <Widget {...basis}><WidgetWert wert={etv ? deDatum(etv.start_datum).slice(0, 6) : '–'} sub={etv ? etv.titel || 'Eigentümerversammlung' : 'Keine ETV geplant'} /></Widget>;
    }
    case 'wegverwalter': {
      // Bestellung je WEG am Objekt (weg_bestellt_bis); bei mehreren WEGs die zuerst endende
      const wegIds = new Set(z.zuordnungen.filter(x => x.rolle === 'WEG' || x.rolle === 'WEG+SEV').map(x => x.objektId));
      const bis = daten.objekte.filter(o => wegIds.has(o.id)).map(o => (o.weg_bestellt_bis ?? '').slice(0, 10)).filter(Boolean).sort()[0];
      const st = bestellungStatus(bis ?? null, s.heute);
      return (
        <Widget {...basis}>
          <WidgetWert wert="ImmoSchwede" sub={bis ? `bestellt bis ${deDatum(bis)} · Vertrag mit der GdWE` : 'Bestellt von der GdWE · Vertrag mit der Gemeinschaft'} />
          {st && <ChipZeile><Chip groesse="xs" ton={st === 'abgelaufen' ? 'bad' : 'warn'}>{st === 'abgelaufen' ? 'Bestellung abgelaufen' : 'Wiederbestellung'}</Chip></ChipZeile>}
        </Widget>
      );
    }
    case 'wegext': {
      const ext = z.zuordnungen.filter(x => x.rolle === 'SEV');
      return (
        <Widget {...basis}>
          <div className="flex flex-1 flex-col justify-center min-w-0" style={{ gap: 7 }}>
            {ext.map(x => {
              const fv = daten.fremdverwalter.find(f => f.objektId === x.objektId);
              return <Zeile key={x.objektId} icon={IdentificationBadge} farbe="var(--info)" text={fv?.name ?? 'Fremdverwalter nicht hinterlegt'} rechts={x.objektName} />;
            })}
            <Zeile icon={Coins} farbe="var(--acc)" text={`Hausgeld ${eur(w.hgExt, { nachkomma: 0 })} / Monat aus Miete`} rechts="Überweisung" />
            <Zeile icon={FileArrowDown} farbe="var(--ink3)" text="Weitergeleitete Unterlagen folgen" />
          </div>
        </Widget>
      );
    }
    case 'bank': {
      const iban = ibanKurz(String(e.bankverbindung ?? ''));
      const inhaber = String(e.kontoinhaber ?? '').trim() || s.name;
      const rhythmus = RHYTHMEN.find(r => r.key === e.auszahlung_rhythmus)?.label.toLowerCase();
      const sub = !iban ? 'Keine Bankverbindung' : w.verwaltet ? `Auszahlung an ${inhaber}${rhythmus ? ` · ${rhythmus}` : ''}` : e.sepa_mandatsreferenz ? 'SEPA-Mandat Hausgeld' : 'Kein SEPA-Mandat';
      return <Widget {...basis}><WidgetWert wert={iban ?? '–'} sub={sub} /></Widget>;
    }
    case 'beteiligte': {
      const b = daten.beteiligte;
      const mit = b.filter(x => x.typ === 'miteigentuemer');
      const vtr = b.filter(x => x.typ === 'vertreter');
      const ausEinheiten = [...new Map(daten.miteigentuemer.map(m => [m.id, m.name])).values()];
      const n = mit.length + ausEinheiten.length;
      const sub = mit.length ? `${mit[0]?.art === 'erbengemeinschaft' ? 'Erbengemeinschaft' : 'Miteigentum'} · ${tausend(eigenerAnteil(b))} % ${s.name}`
        : ausEinheiten.length ? `Miteigentum · ${ausEinheiten[0]}${ausEinheiten.length > 1 ? ` + ${ausEinheiten.length - 1}` : ''}`
          : vtr[0] ? `${artLabel(vtr[0].art)} · ${vtr[0].name}` : 'Alleineigentum';
      return (
        <Widget {...basis}>
          <WidgetWert wert={n + vtr.length} sub={sub} />
          {zustellvertreterFehlt(b) && <ChipZeile><Chip groesse="xs" ton="warn">Zustellvertreter fehlt</Chip></ChipZeile>}
        </Widget>
      );
    }
    case 'zustellung': {
      const weg = versandweg(e.versandweg, s.portal === 'aktiv', s.email);
      const einw = typeof e.textform_einwilligung_am === 'string' && !!e.textform_einwilligung_am;
      const abw = [e.zustell_strasse, e.zustell_plz, e.zustell_stadt].some(v => typeof v === 'string' && v.trim());
      return (
        <Widget {...basis}>
          <WidgetWert wert={VERSANDWEGE.find(v => v.key === weg.weg)?.label ?? '–'} sub={`${abw ? 'abweichende Adresse · ' : ''}${einw ? 'Textform-Einwilligung liegt vor' : 'keine Einwilligung Textform'}`} />
          {textformWarnung(weg.weg, einw ? 'ja' : null) && <ChipZeile><Chip groesse="xs" ton="bad">Einwilligung fehlt</Chip></ChipZeile>}
        </Widget>
      );
    }
    case 'termine': {
      // Termine am Eigentümer und ETV der WEGs, ohne Doppelte (wie im Panel)
      const t = [...new Map([...daten.termine, ...daten.etvTermine].map(x => [x.id, x])).values()].sort((a, b) => a.start_datum.localeCompare(b.start_datum));
      return (
        <Widget {...basis}>
          <WidgetWert wert={t[0] ? deDatum(t[0].start_datum).slice(0, 6) : '–'} sub={t[0] ? t[0].titel || 'Termin' : 'Keine Termine'} />
          {t.length > 0 && <ChipZeile><Chip groesse="xs">{t.length} anstehend</Chip></ChipZeile>}
        </Widget>
      );
    }
    case 'kontakt': {
      const adresse = [[e.strasse, e.hausnummer].filter(Boolean).join(' '), [e.plz, e.stadt].filter(Boolean).join(' ')].filter(Boolean).join(', ');
      return (
        <Widget {...basis}>
          <div className="flex flex-1 flex-col justify-center min-w-0" style={{ gap: 7 }}>
            {s.telefon ? <Zeile icon={Phone} farbe="var(--ok)" text={s.telefon} rechts="Telefon" /> : <Zeile icon={Phone} farbe="var(--ink3)" text="Keine Telefonnummer" />}
            {s.email ? <Zeile icon={EnvelopeSimple} farbe="var(--info)" text={s.email} rechts="E-Mail" /> : <Zeile icon={EnvelopeSimple} farbe="var(--ink3)" text="Keine E-Mail" />}
            {adresse ? <Zeile icon={MapPin} farbe="var(--acc)" text={adresse} rechts={z.firma ? 'Firmensitz' : 'Wohnort'} /> : <Zeile icon={MapPin} farbe="var(--ink3)" text="Keine Anschrift" />}
          </div>
        </Widget>
      );
    }
    case 'portal': {
      const pa = PORTAL_ANZEIGE[s.portal];
      const sub = s.portal === 'aktiv' ? (s.email ? `Login ${s.email}` : 'Gemeinsamer Zugang') : s.portal === 'gesperrt' ? 'Zugang gesperrt' : !s.email ? 'E-Mail fehlt' : 'Zugang freigeben';
      return <Widget {...basis}><WidgetWert wert={<span style={{ fontSize: 24, color: pa.farbe }}>{pa.label}</span>} sub={sub} /></Widget>;
    }
    case 'kommunikation': {
      const liste = daten.aktivitaeten.slice(0, 3);
      return (
        <Widget {...basis}>
          <div className="flex flex-1 flex-col justify-center min-w-0" style={{ gap: 7 }}>
            {liste.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine Kommunikation</span>}
            {liste.map((a, i) => <Zeile key={`${a.typ}-${a.id ?? i}`} icon={AKT_ICON[a.typ]} farbe="var(--acc)" text={a.titel} rechts={deDatum(a.datum)} />)}
          </div>
        </Widget>
      );
    }
    case 'dokumente': {
      const d = daten.dokumente;
      return <Widget {...basis}><WidgetWert wert={d.length} sub={d[0] ? `zuletzt ${d[0].titel || d[0].datei_name || 'Dokument'}` : 'Keine Dokumente'} /></Widget>;
    }
    case 'briefe':
      return <Widget {...basis}><BriefeWidgetWert art="eigentuemer" id={e.id} /></Widget>;
    case 'notizen': {
      const n = daten.notizen;
      return <Widget {...basis}><WidgetWert wert={n.length} sub={n[0] ? n[0].text : 'Keine Notizen'} /></Widget>;
    }
    case 'ki':
      return (
        <Widget {...basis}>
          <span style={{ flex: 1, marginTop: 8, fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}>
            {s.zusammenfassung}
          </span>
        </Widget>
      );
  }
}
